import { Dialog, Button, PlainButton } from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { ReactComponent as CloseIcon } from "@jugl-web/ui-components/web/DrawerHeader/assets/close.svg";
import { v4 as uuidv4 } from "uuid";

import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useForm } from "react-hook-form";
import {
  InventoryItem,
  InventoryItemCategory,
  InventoryItemParams,
} from "@jugl-web/rest-api";
import { inventoryApi } from "@web-src/features/api/createApi";
import { InvoiceItemCustom } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/InvoicesItemFormPageContext";
import { ReactComponent as ItemFilledIcon } from "./assets/item-filled.svg";
import { NameField } from "./components/NameField/NameField";
import { CategoryField } from "./components/CategoryField/CategoryField";
import { DescriptionField } from "./components/DescriptionField/DescriptionField";
import { UnitField } from "./components/UnitField/UnitField";
import { PriceField } from "./components/PriceField/PriceField";
import { QuantityField } from "./components/QuantityField/QuantityField";
import { ServiceFormValues } from "./types";

interface ItemsFormAddServiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (
    params:
      | {
          item: InvoiceItemCustom;
          quantity: number;
          isCustom?: true;
        }
      | {
          item: InventoryItem;
          quantity: number;
          isCustom?: false;
        }
  ) => void;
}

export const ItemsFormAddServiceDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: ItemsFormAddServiceDialogProps) => {
  const [addInventoryItem] = inventoryApi.useAddInventoryItemMutation();
  const { toast } = useToast();
  const { entity } = useEntitySelectedProvider();

  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    register,
  } = useForm<ServiceFormValues>({
    mode: "all",
    shouldUnregister: true,
    resetOptions: {
      keepSubmitCount: false,
    },
    defaultValues: {
      name: "",
      category: InventoryItemCategory.service,
      description: "",
      unit: null,
      price: "",
      quantity: 1,
    },
  });

  const { t } = useTranslations();

  const handleSaveAndAdd = async (data: ServiceFormValues) => {
    const formData: Partial<InventoryItemParams> = {
      inventoryItemName: data.name,
      unit: data.unit,
      description: data.description,
      price: {
        value: data.price,
      },
      category:
        data.category === "service"
          ? InventoryItemCategory.service
          : InventoryItemCategory.item,
    };

    try {
      const response = await addInventoryItem({
        entityId: entity.id,
        params: formData,
        silentError: true,
      });

      if ("error" in response) {
        // @ts-expect-error fix typing
        const firstError = response.error.data.errors[0];
        const customMessage = t({
          id: "inventory.add-item.name-already-exists",
          defaultMessage: "Inventory item with this name already exists",
        });

        if (firstError.message === "name_already_exists") {
          toast(
            firstError.message === "name_already_exists"
              ? customMessage
              : firstError.message,
            {
              variant: "error",
            }
          );
          return;
        }
      }

      if ("data" in response) {
        toast(
          t(
            {
              id: "feedback.inventory-info-has-been-created",
              defaultMessage: "New {category} has been created",
            },
            {
              category: data.category === "service" ? "Service" : "Item",
            }
          ),
          {
            variant: "success",
          }
        );

        onSuccess({
          item: response.data,
          isCustom: false,
          quantity: data.quantity || 1,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        toast(err.message, {
          variant: "error",
        });
      }
    }
  };

  const handleAdd = (data: ServiceFormValues) => {
    const item: InvoiceItemCustom = {
      id: uuidv4(),
      name: data.name,
      category: data.category,
      description: data.description,
      unit: data.unit,
      price: data.price,
      isCustom: true,
      discount: 0,
    };

    onSuccess({ isCustom: true, item, quantity: data.quantity || 1 });
  };

  const handleSubmitForm = (submitFn: (data: ServiceFormValues) => void) => {
    handleSubmit(submitFn)();
  };

  const handleClose = () => {
    onClose();
  };

  const name = watch("name");
  const category = watch("category");
  const description = watch("description");

  const isItem = category === "item";

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {}}
      size="auto"
      className="z-[110] w-[620px] overflow-hidden"
    >
      <>
        <header className=" rounded-t-3xl">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="text-dark font-secondary flex items-center gap-2 text-sm font-semibold">
              <ItemFilledIcon />
              {t({
                id: "invoices.add-new-item",
                defaultMessage: "New Item",
              })}
            </div>
            <PlainButton onClick={handleClose}>
              <CloseIcon />
            </PlainButton>
          </div>
          <div className="bg-dark-100 h-px w-full" />
        </header>

        <form className="jugl_custom-scrollbar max-h-[calc(100vh-7rem)] overflow-y-auto px-14 py-10">
          <div className="space-y-5">
            <NameField
              register={register}
              errors={errors}
              name={name}
              category={category}
            />
            <CategoryField control={control} />
            <DescriptionField
              register={register}
              errors={errors}
              description={description}
            />

            {isItem && <UnitField control={control} errors={errors} />}

            <div className="grid grid-cols-2 gap-4">
              <PriceField control={control} errors={errors} isItem={isItem} />
              <QuantityField
                control={control}
                errors={errors}
                isItem={isItem}
              />
            </div>
          </div>
          <div className="mt-12 flex w-full items-center justify-between gap-4">
            <Button
              className="w-full"
              color="grey"
              type="button"
              onClick={() => handleSubmitForm(handleAdd)}
            >
              {t({
                id: "invoices.add-to-invoice-only",
                defaultMessage: "Add to Invoice Only",
              })}
            </Button>
            <Button
              className="w-full"
              onClick={() => handleSubmitForm(handleSaveAndAdd)}
            >
              {t({
                id: "invoices.add-and-save-to-inventory",
                defaultMessage: "Add and Save to Inventory",
              })}
            </Button>
          </div>
        </form>
      </>
    </Dialog>
  );
};
